import { DocumentType } from "@aws-sdk/types";
import { CreateClaimCommand, UpdateClaimCommand } from "@amzn/ttechclaimintakeservice-client";

import { getAuthenticatedClient } from "src/api/setup/getAuthenticatedClient";
import {
  CLIENT_ERROR_RESPONSES,
  HttpStatusCodes,
  ProcessedApiResponse,
  SERVER_ERROR_RESPONSES,
} from "src/api/types";
import { MetricsPublisher } from "src/metrics/metrics";

import { CreateClaimParams, UpdateClaimParams } from "./types";

// ---------------------------------------------------------------------------
// schemaClaimsService.ts
//
// The generic service for submitting untyped calls to the schema endpoints.
// This service should only be called by the typed-specific schema files that
// map input to the right format and add business rules.
// ---------------------------------------------------------------------------

/**
 * Converts a DocumentType (arbitrary JSON object) into ResponseField[] format
 * used by ProcessedApiResponse. Each top-level key becomes a ResponseField entry.
 */
function documentToResponseFields(
  doc: DocumentType | undefined
): { fieldName: string; fieldValue: string | undefined }[] {
  if (!doc || typeof doc !== "object") {
    return [];
  }
  return Object.entries(doc).map(([key, value]) => ({
    fieldName: key,
    fieldValue: value != null ? String(value) : undefined,
  }));
}

const UNAUTHORIZED_RESPONSE: ProcessedApiResponse = {
  statusCode: HttpStatusCodes.UNAUTHORIZED,
  data: [],
  warningList: [],
};

/**
 * Handles a successful claim response by publishing success metrics
 * and mapping the output to ProcessedApiResponse.
 */
function handleClaimSuccess(
  response: { $metadata?: { httpStatusCode?: number }; claimOutput?: DocumentType },
  metricsPublisher: MetricsPublisher
): ProcessedApiResponse {
  const statusCode = response?.$metadata?.httpStatusCode?.toString() ?? HttpStatusCodes.OK;

  metricsPublisher.publishSubmitClaimLatencyMetrics();
  metricsPublisher.publishClaimSubmittedSuccessMetrics();
  metricsPublisher.publishRenderSuccessMetrics();

  return {
    statusCode,
    data: documentToResponseFields(response.claimOutput),
    warningList: [],
  };
}

/**
 * Handles a failed claim response by publishing error metrics
 * categorized by status code, then re-throws the error.
 */
function handleClaimError(
  error: { $metadata?: { httpStatusCode?: number } },
  metricsPublisher: MetricsPublisher,
  metricsErrorCodePublisher: MetricsPublisher
): never {
  const statusCode = error?.$metadata?.httpStatusCode?.toString() || "Unknown";

  metricsPublisher.publishClaimSubmittedFailure();
  metricsPublisher.publishSubmitClaimLatencyMetrics();
  metricsPublisher.publishRenderFatalMetrics();
  metricsErrorCodePublisher.publishClaimSubmittedErrorCodeMetrics(statusCode);

  if (CLIENT_ERROR_RESPONSES.includes(statusCode as HttpStatusCodes)) {
    metricsPublisher.publishClaimSubmittedClientErrorMetrics();
    if (statusCode === HttpStatusCodes.BAD_REQUEST) {
      metricsPublisher.claimValidationError();
    }
  }

  if (SERVER_ERROR_RESPONSES.includes(statusCode as HttpStatusCodes)) {
    metricsPublisher.publishClaimSubmittedServerFailureMetrics();
    if (statusCode === HttpStatusCodes.GATEWAY_TIMEOUT) {
      metricsPublisher.publishClaimSubmittedTimeoutMetrics();
    }
  }

  throw error;
}

/**
 * CreateClaim generic service call
 * Takes a generic input and does the authentication, service call and error handling
 */
export async function createClaim<TInput extends DocumentType = DocumentType>(
  params: CreateClaimParams<TInput>
): Promise<ProcessedApiResponse> {
  const metricsPublisher = new MetricsPublisher("api.schemaClaims.createClaim");
  const metricsErrorCodePublisher = new MetricsPublisher("schemaClaims.createClaim.errorCode");

  const { client } = await getAuthenticatedClient();
  if (!client) {
    return UNAUTHORIZED_RESPONSE;
  }

  const command = new CreateClaimCommand({
    schemaId: params.schemaId,
    claimInput: params.claimInput as DocumentType,
    testRecord: params.testRecord,
  });

  return await client
    .send(command)
    .then((response) => handleClaimSuccess(response, metricsPublisher))
    .catch((error: { $metadata?: { httpStatusCode?: number } }) =>
      handleClaimError(error, metricsPublisher, metricsErrorCodePublisher)
    );
}

/**
 * UpdateClaim generic service call
 * Takes a generic input and does the authentication, service call and error handling
 */
export async function updateClaim<TInput extends DocumentType = DocumentType>(
  params: UpdateClaimParams<TInput>
): Promise<ProcessedApiResponse> {
  const metricsPublisher = new MetricsPublisher("api.schemaClaims.updateClaim");
  const metricsErrorCodePublisher = new MetricsPublisher("schemaClaims.updateClaim.errorCode");

  const { client } = await getAuthenticatedClient();
  if (!client) {
    return UNAUTHORIZED_RESPONSE;
  }

  const command = new UpdateClaimCommand({
    schemaId: params.schemaId,
    stage: params.stage,
    claimNumber: params.claimNumber,
    claimInput: params.claimInput as DocumentType,
    testRecord: params.testRecord,
  });

  return await client
    .send(command)
    .then((response) => handleClaimSuccess(response, metricsPublisher))
    .catch((error: { $metadata?: { httpStatusCode?: number } }) =>
      handleClaimError(error, metricsPublisher, metricsErrorCodePublisher)
    );
}
